if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      if(this.form) {
        this.form.querySelector('[name=id]').disabled = false
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
      }
      
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
      this.quantityOverLimit = false;
      this.onQuantityInputChange = this.onQuantityInputChange.bind(this);
    }

    connectedCallback() {
      this.quantityInput = this.querySelector('[name="quantity"]');
      if (!this.quantityInput) return;
      this.quantityInput.addEventListener('change', this.onQuantityInputChange);
      this.quantityInput.addEventListener('input', this.onQuantityInputChange);
      this.validateQuantityAvailability();
    }

    disconnectedCallback() {
      if (this.quantityInput) {
        this.quantityInput.removeEventListener('change', this.onQuantityInputChange);
        this.quantityInput.removeEventListener('input', this.onQuantityInputChange);
      }
    }

    onQuantityInputChange() {
      this.validateQuantityAvailability();
    }

    getStickyAtc() {
      const id = this.submitButton?.id;
      if (!id) return null;
      return document.querySelector(`sticky-atc[data-submit-id="${id}"]`);
    }

    isAddButtonLocked() {
      const status = this.submitButton?.dataset.status;
      return status === 'sold-out' || status === 'unavailable' || status === 'limited-stock';
    }

    getAvailableInventory() {
      this.syncInventoryForVariant();
      const input = this.quantityInput;
      if (!input) return null;

      const management = input.dataset.inventoryManagement;
      const policy = input.dataset.inventoryPolicy;
      const available = parseInt(input.dataset.inventoryQuantity, 10);

      if (!management || policy === 'continue' || Number.isNaN(available) || available <= 0) {
        return null;
      }
      return available;
    }

    syncInventoryForVariant() {
      const variantId = this.form?.querySelector('[name="id"]')?.value;
      if (!variantId) return;

      if (!this._inventoryMap) {
        const script = this.querySelector('[data-variant-inventory]');
        if (!script) return;
        try {
          const list = JSON.parse(script.textContent);
          this._inventoryMap = Object.fromEntries(
            (list || []).map((variant) => [String(variant.id), variant]),
          );
        } catch (error) {
          this._inventoryMap = {};
        }
      }

      const data = this._inventoryMap[String(variantId)];
      if (!data) return;

      const apply = (input) => {
        if (!input) return;
        input.dataset.inventoryQuantity = String(data.quantity ?? '');
        input.dataset.inventoryPolicy = data.policy || '';
        input.dataset.inventoryManagement = data.management || '';
      };

      apply(this.quantityInput);
      apply(this.getStickyAtc()?.querySelector('.quantity__input'));
    }

    getSubmitButtons() {
      return [this.submitButton, this.getStickyAtc()?.querySelector('[type="submit"]')].filter(Boolean);
    }

    setSubmitLoading(loading) {
      this.getSubmitButtons().forEach((button) => {
        button.classList.toggle('loading', loading);
        if (loading) button.setAttribute('aria-disabled', 'true');
      });
    }

    setAddButtonsDisabled(disabled) {
      const buttons = this.getSubmitButtons();
      buttons.forEach((button) => {
        if (disabled) {
          button.setAttribute('disabled', 'true');
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        }
      });

      const buyNow = this.querySelector('[data-buy-now-button]');
      if (buyNow) buyNow.disabled = disabled;
    }

    validateQuantityAvailability() {
      if (!this.quantityInput || this.isAddButtonLocked()) return;

      const available = this.getAvailableInventory();
      const requested = parseInt(this.quantityInput.value, 10) || 1;
      const overLimit = available !== null && requested > available;

      if (overLimit) {
        this.quantityOverLimit = true;
        const message = (window.cartStrings?.quantityAvailabilityError || '')
          .replace('[quantity]', String(available));
        this.handleErrorMessage(message);
        this.setAddButtonsDisabled(true);
        return;
      }

      if (this.quantityOverLimit) {
        this.quantityOverLimit = false;
        this.handleErrorMessage();
        this.setAddButtonsDisabled(false);
      }
    }

    syncStickyError(errorMessage = false) {
      const stickyError = this.getStickyAtc()?.querySelector('.sticky-atc__error');
      if (!stickyError) return;
      stickyError.toggleAttribute('hidden', !errorMessage);
      stickyError.textContent = errorMessage || '';
    }

    isPartialAvailabilityAdd(response) {
      const text = `${response?.description || ''} ${response?.message || ''}`;
      return /were added to your cart due to availability/i.test(text);
    }

    openCartAfterPartialAdd() {
      if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return;

      const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
      fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)
        .then((response) => response.json())
        .then((sections) => {
          this.cart.renderContents({ sections });
        })
        .then(() => fetch(`${window.routes.cart_url}.js`))
        .then((response) => response.json())
        .then((cart) => {
          document.dispatchEvent(
            new CustomEvent('cart:change', {
              detail: {
                cart,
                sectionId: this.dataset.source,
              },
            }),
          );
        })
        .catch((error) => {
          console.error(error);
        });
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      this.validateQuantityAvailability();
      if (this.quantityOverLimit || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.setSubmitLoading(true);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      } else if (!window.cartStrings?.open_drawer_when_add_to_cart) {
        // Page cart type, no redirect — fetch the cart icon section so the count updates
        formData.append('sections', ['cart-icon-bubble']);
        formData.append('sections_url', window.location.pathname);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {

            // dispatch cart:error
						document.dispatchEvent(
							new CustomEvent('cart:error', {
								detail: {
									source: this.dataset.source,
									productVariantId: formData.get('id'),
									errors: response.description,
									message: response.message,
								},
							})
						)
						// dispatch cart:error

            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);

            if (this.isPartialAvailabilityAdd(response)) {
              this.error = false;
              this.openCartAfterPartialAdd();
              return;
            }

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            if (window.cartStrings?.open_drawer_when_add_to_cart) {
              window.location = window.routes.cart_url;
            } else {
              // Page cart type, no redirect — update the cart icon count in place
              const iconBubble = document.getElementById('cart-icon-bubble');
              if (iconBubble && response.sections?.['cart-icon-bubble']) {
                iconBubble.innerHTML = new DOMParser()
                  .parseFromString(response.sections['cart-icon-bubble'], 'text/html')
                  .querySelector('.shopify-section').innerHTML;
              }
              if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
            }
            return;
          }

          // dispatch variant:add
					document.dispatchEvent(
						new CustomEvent('variant:add', {
							detail: {
								variant: {
									id: formData.get('id'),
								},
								quantity: Number(formData.get('quantity') || 1),
								formElement: this.form,
								sectionId: this.dataset.source,
							},
						})
					)
					// dispatch variant:add

          fetch(`${routes.cart_url}.js`)
						.then((response) => {
							return response.text()
						})
						.then((state) => {
							const parsedState = JSON.parse(state)

							// dispatch cart:change for the entire basket
							document.dispatchEvent(
								new CustomEvent('cart:change', {
									detail: {
										cart: parsedState,
										sectionId: this.dataset.source,
									},
								})
							)
							// dispatch cart:change for the entire basket
          })
          .catch((error) => {
            console.error('Error fetching cart state:', error)
          })

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.setSubmitLoading(false);
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.validateQuantityAvailability();
          if (!this.error && !this.quantityOverLimit && !this.isAddButtonLocked()) {
            this.submitButton.removeAttribute('aria-disabled');
            const stickySubmit = this.getStickyAtc()?.querySelector('[type="submit"]');
            if (stickySubmit && !stickySubmit.hasAttribute('disabled')) {
              stickySubmit.removeAttribute('aria-disabled');
            }
          }
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (this.errorMessageWrapper) {
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      this.syncStickyError(errorMessage);
    }
  });
}
